import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import portalAdminService from '../../services/portalAdminService';
import { useAuth, useDialog } from '../../context';
import PortalShell from './PortalShell';

/**
 * /portal/admin/admins — owner-only management.
 * Includes the TRANSFER OWNERSHIP form with handle-confirmation per spec.
 */
export default function PortalAdminAdminsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showConfirm } = useDialog();
  const [rows, setRows] = useState([]);
  const [memberId, setMemberId] = useState('');
  const [members, setMembers] = useState([]);
  const [transferTarget, setTransferTarget] = useState('');
  const [transferHandle, setTransferHandle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const isOwner = user?.portalRole === 'owner';

  useEffect(() => {
    if (!isOwner) navigate('/noir/admin', { replace: true });
  }, [isOwner, navigate]);

  const refresh = async () => {
    const r = await portalAdminService.listAdmins();
    if (r.success) setRows(r.data || []);
    const m = await portalAdminService.listMembers({ pageSize: 100 });
    if (m.success) setMembers((m.data || []).filter((x) => x.portalRole === 'member'));
  };
  useEffect(() => { refresh(); }, []);

  const promote = async () => {
    if (!memberId) return;
    setBusy(true); setError(null);
    const r = await portalAdminService.promoteAdmin(memberId);
    setBusy(false);
    if (r.success) { setMemberId(''); refresh(); }
    else setError(r.error || 'Could not promote');
  };

  const demote = async (uid) => {
    const ok = await showConfirm({ title: 'Demote admin', message: 'They go back to member. They keep their Portal Noir access.', confirmLabel: 'DEMOTE' });
    if (!ok) return;
    setBusy(true); setError(null);
    const r = await portalAdminService.demoteAdmin(uid);
    setBusy(false);
    if (r.success) refresh();
    else setError(r.error || 'Could not demote');
  };

  const promoteOwner = async () => {
    if (!transferTarget || !transferHandle) { setError('Pick the admin and type their handle to confirm.'); return; }
    setBusy(true); setError(null);
    const r = await portalAdminService.promoteToOwner({ newOwnerUserId: transferTarget, confirmHandle: transferHandle });
    setBusy(false);
    if (r.success) { setTransferTarget(''); setTransferHandle(''); refresh(); }
    else setError(r.error || 'Could not promote to owner');
  };

  const stepDown = async () => {
    const ok = await showConfirm({ title: 'Step down as owner', message: 'You\'ll become an admin. There must be at least one other owner standing.', confirmLabel: 'STEP DOWN', variant: 'danger' });
    if (!ok) return;
    setBusy(true); setError(null);
    const r = await portalAdminService.stepDownFromOwner();
    setBusy(false);
    if (r.success) refresh();
    else setError(r.error || 'Could not step down');
  };

  return (
    <PortalShell viewerState="accepted">
      <div style={{ maxWidth: 960, margin: '32px auto 80px', padding: '0 24px' }}>
        <button onClick={() => navigate('/noir/admin')} className="portal-mono" style={{ background: 'none', border: 'none', color: 'rgba(236,231,218,0.34)', cursor: 'pointer', fontSize: 11, marginBottom: 24 }}>← ADMIN</button>
        <p className="portal-label" style={{ marginBottom: 14 }}>ADMINS (OWNER ONLY)</p>
        <h1 className="portal-hero" style={{ marginBottom: 28 }}>Admins.</h1>
        {error && <p className="portal-mono" style={{ color: '#B4736A' }}>{error}</p>}

        <div className="portal-card" style={{ padding: 0, marginBottom: 28 }}>
          {rows.length === 0 ? (
            <p className="portal-mono" style={{ color: 'rgba(236,231,218,0.34)', padding: 18, fontSize: 11 }}>NO ADMINS YET.</p>
          ) : rows.map((m) => (
            <div key={m.userId} style={{ padding: '14px 18px', borderBottom: '1px solid rgba(236,231,218,0.12)', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div className="portal-avatar">
                {m.avatarUrl ? <img src={m.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="portal-serif" style={{ fontSize: 17 }}>{m.displayName}</div>
                <div className="portal-mono" style={{ fontSize: 10, color: 'rgba(236,231,218,0.34)' }}>
                  {m.handle ? `@${m.handle}` : ''} · {(m.portalRole || 'admin').toUpperCase()}
                </div>
              </div>
              {m.portalRole === 'admin' && (
                <button onClick={() => demote(m.userId)} disabled={busy} className="portal-btn ghost" style={{ padding: '8px 14px', fontSize: 10 }}>REMOVE</button>
              )}
            </div>
          ))}
        </div>

        <p className="portal-label" style={{ marginBottom: 10 }}>ASSIGN ADMIN</p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 32 }}>
          <select className="portal-input" value={memberId} onChange={(e) => setMemberId(e.target.value)}>
            <option value="">Pick a member…</option>
            {members.map((m) => <option key={m.userId} value={m.userId}>{m.displayName} {m.handle ? `· @${m.handle}` : ''}</option>)}
          </select>
          <button onClick={promote} disabled={busy || !memberId} className="portal-btn solid">+ ASSIGN ADMIN</button>
        </div>

        <hr className="portal-hr" />

        <p className="portal-label" style={{ marginBottom: 10 }}>PROMOTE TO OWNER</p>
        <p className="portal-serif" style={{ color: 'rgba(236,231,218,0.56)', marginBottom: 16, fontStyle: 'italic' }}>
          Promotes an admin to a full owner. Portal Noir supports multiple owners — promoting another doesn't demote you. Confirm with the target's handle to avoid mistakes.
        </p>
        <div style={{ display: 'grid', gap: 10, maxWidth: 540, marginBottom: 32 }}>
          <select className="portal-input" value={transferTarget} onChange={(e) => setTransferTarget(e.target.value)}>
            <option value="">Pick an admin…</option>
            {rows.filter((r) => r.portalRole === 'admin').map((m) => <option key={m.userId} value={m.userId}>{m.displayName} {m.handle ? `· @${m.handle}` : ''}</option>)}
          </select>
          <input className="portal-input" placeholder="Type their handle to confirm" value={transferHandle} onChange={(e) => setTransferHandle(e.target.value)} />
          <button onClick={promoteOwner} disabled={busy || !transferTarget || !transferHandle} className="portal-btn solid">PROMOTE TO OWNER</button>
        </div>

        <p className="portal-label" style={{ marginBottom: 10 }}>STEP DOWN</p>
        <p className="portal-serif" style={{ color: 'rgba(236,231,218,0.56)', marginBottom: 16, fontStyle: 'italic' }}>
          Give up your owner role and revert to admin. Blocked when you're the last owner standing.
        </p>
        <button onClick={stepDown} disabled={busy} className="portal-btn danger">STEP DOWN AS OWNER</button>
      </div>
    </PortalShell>
  );
}
